import type { ReportErrorOptions } from '../types'
import { isServer, isProduction } from '../utils'
import type { MonitoringProvider } from './types'

/**
 * Console provider that logs errors to console for debugging
 *
 * Uses structured JSON on server, human-readable format in browser
 * Always logs all errors (does not filter 404s)
 */
export const createConsoleProvider = (): MonitoringProvider => ({
  trackError(
    error: Error | string,
    props?: Record<string, string | number>,
    _options?: ReportErrorOptions,
  ): void {
    // Console provider ignores options and logs everything for debugging
    if (isServer()) {
      logServerError(error, props)
    } else {
      logBrowserError(error, props)
    }
  },

  addPageAction(): void {
    // Console provider doesn't log page actions (too noisy)
  },
})

function logBrowserError(
  error: Error | string,
  props?: Record<string, string | number>,
): void {
  // eslint-disable-next-line no-console
  console.error('[Monitoring]', error, props ?? {})
}

function logServerError(
  error: Error | string,
  props?: Record<string, string | number>,
): void {
  const errorMessage = typeof error === 'string' ? error : error.message
  const errorStack = error instanceof Error ? error.stack : undefined
  const errorName = error instanceof Error ? error.name : 'Error'

  const logEntry = {
    level: 'error',
    source: 'monitoring',
    message: errorMessage,
    error: {
      name: errorName,
      message: errorMessage,
      stack: errorStack,
    },
    props: props ?? {},
    timestamp: new Date().toISOString(),
  }

  const spacing = isProduction() ? 0 : 2
  // eslint-disable-next-line no-console
  console.error(JSON.stringify(logEntry, null, spacing))
}
